import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import _debounce from "lodash/debounce";

import SubscriptionsPage from "../components/SubscriptionsPage";
import useSubscriptionList from "../hooks/useSubscriptionList";

/**
 * How long to wait after the last keystroke before asking the server.
 *
 * Debounced here rather than inside the shared `Search` component, which `/orders` also uses and which
 * filters an already-loaded collection in memory — it has nothing to debounce.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The order the page has always presented, now expressed as server parameters.
 *
 * `renewalDate` is the derived key the old client-side comparator used: the end date while the
 * subscription is active, otherwise the next payment date. Both halves are load-bearing — sorting on
 * either column alone silently reorders the page.
 */
const SORT = Object.freeze( { sort: "renewalDate", order: "asc" } );

/**
 * The sections, in the order they stack down the page.
 *
 * `needsAttention` first is load-bearing rather than cosmetic: it holds lapsed subscriptions the customer
 * can still renew, so it is the one section that has to be seen without being looked for.
 */
const SECTIONS = Object.freeze( [ "needsAttention", "provisioned", "regular", "inactive" ] );

/** One page number per section, since each pages independently. */
const FIRST_PAGES = Object.freeze( { needsAttention: 1, provisioned: 1, regular: 1, inactive: 1 } );

/**
 * The customer's subscriptions overview.
 *
 * The page reads bounded pages of finished rows from `Customers/:customerId/subscriptions/list`. That
 * replaces the unbounded subscription collection and, with it, the twenty-odd predicates that used to
 * search, split, sort and group it in the browser — for the largest accounts a 48 MB response that took
 * the server past its memory ceiling.
 *
 * Three fetches go with it: the collection itself, the provisioner catalogue (the two fields a row needs
 * are resolved server-side now) and the orders list, whose result was never read.
 *
 * **The sections are stacked, and each pages independently.** One paginated window cannot span sections
 * without a page boundary falling inside one, which is the same incoherence that made client-side grouping
 * untenable. So each section asks for its own page: four bounded requests on a load rather than one
 * unbounded one, and paging one section leaves the others alone rather than invalidating a combined
 * payload.
 *
 * View state is local rather than in the URL, matching the sites overview. The trade-off is deliberate
 * and visible: returning from a subscription's detail page lands on the first page with the search
 * cleared, and a filtered view cannot be shared. Putting it in the URL would emit a router navigation per
 * keystroke, and each of those is a data-layer event.
 *
 * @returns {React.JSX.Element} The rendered subscriptions page.
 */
function SubscriptionsPageContainer() {
	const dispatch = useDispatch();

	// `query` is what the input shows; `search` is what the server has been asked for. Keeping them apart
	// is what lets the field stay responsive while requests stay debounced.
	const [ query, setQuery ] = useState( "" );
	const [ search, setSearch ] = useState( "" );
	const [ pages, setPages ] = useState( FIRST_PAGES );

	/**
	 * The parameters for one section's page.
	 *
	 * @param {string} bucket The section.
	 *
	 * @returns {Object} The list parameters.
	 */
	const paramsFor = useCallback(
		bucket => ( { ...SORT, bucket, page: pages[ bucket ], ...( search ? { query: search } : {} ) } ),
		[ pages, search ],
	);

	// One call per section. They share a query shape but not a cache entry, because every parameter is part
	// of the key — which is what lets one section page without disturbing the others. `bucketCounts` is
	// account-wide, so all four responses carry the same counts and any of them can supply the headings.
	const needsAttention = useSubscriptionList( paramsFor( "needsAttention" ) );
	const provisioned    = useSubscriptionList( paramsFor( "provisioned" ) );
	const regular        = useSubscriptionList( paramsFor( "regular" ) );
	const inactive       = useSubscriptionList( paramsFor( "inactive" ) );

	const sections = { needsAttention, provisioned, regular, inactive };

	// How far each section can be paged, or null while it is still settling — placeholder data describes the
	// previous query, and a `totalPages` of 0 means there is nothing to clamp to rather than that page 1 is
	// wrong. Serialised because `sections` and each hook's return are fresh objects every render, so no memo
	// over them could ever hit and an effect keyed on them would run every time.
	const pageLimits = JSON.stringify( Object.fromEntries( SECTIONS.map( bucket => {
		const { meta, isLoading, isPlaceholderData } = sections[ bucket ];
		const settled = ! isLoading && ! isPlaceholderData && meta.totalPages > 0;

		return [ bucket, settled ? meta.totalPages : null ];
	} ) ) );

	// The counts are identical in every response, so this picks the first section that has actually answered
	// rather than choosing between conflicting answers. Without the `isLoading` test an unresolved section's
	// zeroed placeholder would win, and the page would decide it has no subscriptions at all.
	const answered = SECTIONS.map( bucket => sections[ bucket ] )
		.find( section => ! section.isLoading && ! section.isError );
	const bucketCounts = ( answered ?? needsAttention ).bucketCounts;

	// Debounced in a memo so the timer survives re-renders; without that, every keystroke would create a
	// new debounced function and none of them would ever be superseded.
	const commitSearch = useMemo(
		() => _debounce( value => {
			setSearch( value );
			// A new search invalidates every page number: page 4 of the old result set is very likely past the
			// end of the new one, which would render an empty section with rows available.
			setPages( FIRST_PAGES );
		}, SEARCH_DEBOUNCE_MS ),
		[],
	);

	useEffect( () => () => commitSearch.cancel(), [ commitSearch ] );

	const onSearchChange = useCallback( value => {
		setQuery( value );
		commitSearch( value );
	}, [ commitSearch ] );

	const onPageChange = useCallback( ( bucket, page ) => {
		setPages( current => ( { ...current, [ bucket ]: page } ) );
	}, [] );

	const onManage = useCallback(
		subscriptionId => dispatch( push( "/account/subscriptions/" + subscriptionId ) ),
		[ dispatch ],
	);

	// A section's result set can shrink under a page number the user never changed. Data here is revalidated
	// on every window focus, so cancelling a subscription in another tab — or one lapsing into a different
	// section — can leave that section past the end of a shorter list, rendering nothing beside a footer
	// counting rows that are gone.
	useEffect( () => {
		const limits = JSON.parse( pageLimits );

		setPages( current => {
			const corrected = { ...current };
			let changed = false;

			for ( const [ bucket, limit ] of Object.entries( limits ) ) {
				// Null means that section has not settled, so there is nothing to clamp against yet.
				if ( limit !== null && current[ bucket ] > limit ) {
					corrected[ bucket ] = limit;
					changed = true;
				}
			}

			// Returning `current` unchanged when nothing moved is what keeps this from looping: a fresh object
			// every time would re-run the effect forever.
			return changed ? corrected : current;
		} );
	}, [ pageLimits ] );

	return <SubscriptionsPage
		sections={ sections }
		bucketCounts={ bucketCounts }
		query={ query }
		onSearchChange={ onSearchChange }
		onPageChange={ onPageChange }
		onManage={ onManage }
	/>;
}

export default SubscriptionsPageContainer;
